'use client'

import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
} from 'recharts'
import { CHART_PALETTES, CHART_GROUP_COLORS, useChartPalette } from '@/lib/chart-theme'
import {
  type ChartSpec,
  normalizeChartSeries,
  scatterGroupField,
  scatterGroupValues,
  scatterPointLabelField,
} from '@/lib/sat/chart-spec'
import { chartAriaLabel, chartDataTable } from '@/lib/sat/chart-aria'
import { FIGURE_TEXT_ATTR } from '@/lib/render/figure-text'

/** Title-case a raw group value ("manufacturing" → "Manufacturing") for legends. */
function labelFor(v: string): string {
  return v.charAt(0).toUpperCase() + v.slice(1)
}

export type { ChartSpec }

// Moved to lib/sat/chart-spec so server code (chart-aria, the mobile figure
// builder) can use it without importing this 'use client' module.
export { normalizeChartSeries, type ChartSeries } from '@/lib/sat/chart-spec'

// Charts adapt to their surface. Inside the Runner's `force-light` wrapper
// (the test is always light "paper") we MUST stay light regardless of the
// app theme — pass `forceLight`. Theme-awareness there fights force-light: it
// produced a dark card on white paper, and the recharts HTML legend got
// force-light'd to dark text on that dark card (invisible).
//
// On every OTHER surface (practice/review/drills — StemRenderer now renders
// charts there too), the chart follows the app theme so a dark-mode page no
// longer shows a jarring white box. `forceLight` defaults to false; the Runner
// (and HighlightableStem) opt in.
export function QuestionChart({
  spec,
  forceLight = false,
}: {
  spec: ChartSpec
  forceLight?: boolean
}) {
  const { type, title, xAxis, yAxis, data } = spec
  // Coerce a possibly-malformed banked spec to a safe series list (missing
  // array → [], missing name → key) so a bad spec degrades instead of throwing.
  const series = normalizeChartSeries(spec)
  // Hook is called unconditionally (rules of hooks); we just ignore its
  // result when forceLight pins the palette to light.
  const themePalette = useChartPalette()
  const palette = forceLight ? CHART_PALETTES.light : themePalette

  const axisTick = { fontSize: 12, fill: palette.tick }

  const sharedProps = {
    data,
    // Extra left room for the rotated Y-axis title (it sits left of the tick
    // numbers) and bottom room for the X-axis title. The legend lives at the
    // TOP (see below), so it no longer competes with the X-axis title.
    margin: { top: 8, right: 20, bottom: 36, left: 28 },
  }

  const xAxisEl = (
    <XAxis
      dataKey={xAxis.key}
      tick={axisTick}
      stroke={palette.axisLine}
      label={{
        value: xAxis.label,
        position: 'insideBottom',
        offset: -8,
        fontSize: 12,
        fill: palette.tick,
        // Center the title under the axis.
        style: { textAnchor: 'middle' },
      }}
      type={type === 'scatter' ? 'number' : undefined}
    />
  )

  const yAxisEl = (
    <YAxis
      // Scatter needs an explicit y dataKey to build its domain; line/bar
      // derive y from the series dataKey, so leave it undefined there.
      dataKey={type === 'scatter' ? yAxis.key : undefined}
      tick={axisTick}
      stroke={palette.axisLine}
      width={56}
      label={{
        value: yAxis.label,
        angle: -90,
        position: 'insideLeft',
        fontSize: 12,
        fill: palette.tick,
        // Without textAnchor:middle the rotated title anchors at one end and
        // runs off the top of the chart (clipped → unreadable). Centering it
        // along the axis is the fix.
        style: { textAnchor: 'middle' },
      }}
      type={type === 'scatter' ? 'number' : undefined}
    />
  )

  const grid = <CartesianGrid strokeDasharray="3 3" stroke={palette.grid} />
  const tooltip = (
    <Tooltip
      contentStyle={{
        fontSize: 12,
        background: palette.tooltipBg,
        border: `1px solid ${palette.tooltipBorder}`,
        borderRadius: 8,
        color: palette.tooltipText,
      }}
      labelStyle={{ color: palette.tooltipText }}
      itemStyle={{ color: palette.tooltipText }}
    />
  )
  // Legend at the TOP (under the title), not the bottom — at the bottom it
  // overlapped the X-axis title. recharts reserves its own band for it, so the
  // plot shrinks to fit cleanly.
  const legend = (
    <Legend verticalAlign="top" align="center" wrapperStyle={{ fontSize: 12, paddingBottom: 8 }} />
  )

  let chart: React.ReactNode

  if (type === 'bar' || type === 'histogram') {
    // A histogram is a bar chart over contiguous bins, so we render bars
    // with no gap between categories to get the touching-bars look.
    chart = (
      <BarChart {...sharedProps} barCategoryGap={type === 'histogram' ? 0 : undefined}>
        {grid}
        {xAxisEl}
        {yAxisEl}
        {tooltip}
        {legend}
        {series.map((s) => (
          // Value labels on each bar: many data-analysis questions ask the
          // student to read EXACT values and compute (e.g. a region's share),
          // which is impossible when bars fall between coarse gridlines. The
          // label removes the guesswork.
          <Bar key={s.key} dataKey={s.key} name={s.name} fill={s.color} isAnimationActive={false}>
            <LabelList dataKey={s.key} position="top" fontSize={11} fill={palette.tick} />
          </Bar>
        ))}
      </BarChart>
    )
  } else if (type === 'scatter') {
    // Multi-group scatter: split points by a categorical field (e.g. "industry")
    // into distinctly-colored, legend-labelled series. Without this, every series
    // overdrew the SAME full dataset in one color, so groups were indistinguishable
    // (and a single nameless series rendered a lone legend dot). Auto-detected from
    // the data when `groupBy` isn't set, so existing specs render correctly.
    const groupField = scatterGroupField(spec)
    const groups = groupField ? scatterGroupValues(spec, groupField) : []
    // Each point's own name ("Student H") when the question refers to points
    // by it — without it the dots are anonymous and the question unanswerable.
    const labelField = scatterPointLabelField(spec)
    const pointLabels = labelField ? (
      <LabelList
        dataKey={labelField}
        position="top"
        fontSize={11}
        fontWeight={600}
        fill={palette.tick}
      />
    ) : null

    if (groupField && groups.length > 1) {
      chart = (
        <ScatterChart margin={sharedProps.margin}>
          {grid}
          {xAxisEl}
          {yAxisEl}
          {tooltip}
          {legend}
          {groups.map((g, i) => (
            <Scatter
              key={g}
              name={labelFor(g)}
              data={data.filter((d) => d[groupField] === g)}
              fill={CHART_GROUP_COLORS[i % CHART_GROUP_COLORS.length]}
              isAnimationActive={false}
            >
              {pointLabels}
            </Scatter>
          ))}
        </ScatterChart>
      )
    } else {
      // Single-group scatter: one colored series. Show the legend only if the
      // series carries a real name (otherwise it's a meaningless lone dot).
      const s = series[0]
      const named = !!s?.name
      chart = (
        <ScatterChart margin={sharedProps.margin}>
          {grid}
          {xAxisEl}
          {yAxisEl}
          {tooltip}
          {named && legend}
          <Scatter
            name={s?.name}
            data={data}
            fill={s?.color ?? CHART_GROUP_COLORS[0]}
            isAnimationActive={false}
          >
            {pointLabels}
          </Scatter>
        </ScatterChart>
      )
    }
  } else {
    chart = (
      <LineChart {...sharedProps}>
        {grid}
        {xAxisEl}
        {yAxisEl}
        {tooltip}
        {legend}
        {series.map((s) => (
          <Line
            key={s.key}
            type="monotone"
            dataKey={s.key}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            dot={{ r: 4, fill: s.color }}
            isAnimationActive={false}
            connectNulls
          />
        ))}
      </LineChart>
    )
  }

  // Card chrome: fixed light (white card, slate title) inside force-light;
  // theme tokens (bg-card / border / foreground) elsewhere so it flips with
  // the app theme instead of being a white box on a dark page.
  const cardClass = forceLight ? 'border-slate-200 bg-white' : 'border-border bg-card'
  const titleClass = forceLight ? 'text-slate-700' : 'text-foreground'

  // Screen readers can't see the bars/points, so the whole card is one labeled
  // image whose aria-label carries the data; the recharts SVG internals are
  // hidden so SR doesn't read meaningless rendering primitives. When there are
  // too many values for the label to read out, it gives a summary and the full
  // values follow in a visually hidden table (navigable cell by cell). The
  // table is marked as figure text so passage-highlight offsets ignore it.
  const table = chartDataTable(spec)
  return (
    <>
      <div
        className={`my-4 mx-auto max-w-[500px] rounded-lg border p-3 ${cardClass}`}
        role="img"
        aria-label={chartAriaLabel(spec)}
      >
        {title && <p className={`mb-2 text-center text-sm font-medium ${titleClass}`}>{title}</p>}
        <div aria-hidden="true">
          <ResponsiveContainer width="100%" height={320}>
            {chart}
          </ResponsiveContainer>
        </div>
      </div>
      {table && (
        <div className="sr-only" {...{ [FIGURE_TEXT_ATTR]: '' }}>
          <table>
            <caption>{table.caption}</caption>
            <thead>
              <tr>
                {table.columns.map((c, i) => (
                  <th key={i} scope="col">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((r, i) => (
                <tr key={i}>
                  {r.map((c, j) =>
                    j === 0 ? (
                      <th key={j} scope="row">
                        {c}
                      </th>
                    ) : (
                      <td key={j}>{c}</td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}
